import { BlobServiceClient } from "@azure/storage-blob";
import React, { useState } from "react";
import { Buffer as BufferT } from "buffer";
import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import { getSasToken } from "./fileUtils";
import Config from "../config/env";

export type CommonMessageAttachmentTags = {
  entity: string;
  service: string;
  process: string;
  country: string;
  is_public: "true" | "false";
};

export type FileData = {
  category: string;
  subCategory: string;
  description?: string;
};

export type RequestReasonCodes =
  | "SERVICE_QUERY"
  | "ADDITIONAL_SERVICE_REQUEST"
  | "FEES_QUERY"
  | "TECH_SUPPORT";

export type FileObjectType =
  | "AD_HOC"
  | "ESCALATION"
  | "PROCESS"
  | "SHAREHOLDER"
  | "OFFICER"
  | "ENTITIES_PLUS";

export const REQUEST_REASON_CODES = {
  "Service Query": "SERVICE_QUERY",
  "New / Additional Service Request": "ADDITIONAL_SERVICE_REQUEST",
  "Contract / Fees query": "FEES_QUERY",
  "Tech Support (IT/Helpdesk)": "TECH_SUPPORT",
  Implementation: "IMPLEMENTATION",
};

export type IndividualFileData = {
  category: string;
  subCategory: string;
  description?: string;
};

export type CommonAdHocRequestAttachmentTags = {
  entity: string;
  country: string;
  service: string;
  request: string;
  request_reason: RequestReasonCodes;
};

export type CommonEscalationAttachmentTags = {
  entity: string;
  country: string;
  escalated_process: string;
  escalation: string;
};

export type CommonShareHolderTags = {
  entity: string;
  country: string;
  shareHolder: string;
};

export type CommonOfficerTags = {
  entity: string;
  country: string;
  officer: string;
};

export type CommonEntitiesPlusTags = {
  entity: string;
  country: string;
  core_entity_info: "Core Entity Information";
};
export type DocumentVersionResponse = {
  success: boolean;
  data: DocumentVersionData;
  message: string;
  statusCode: number;
  status: boolean;
};
export type DocumentVersionData = {
  message: string;
  latestVersion: number;
};

const useFileUpload = () => {
  const storageAccount = Config.BLOB_STORAGE_ACCOUNT;
  const containerName = "document-repo";
  const BLOB_SAS_URL = `https://${storageAccount}.blob.core.windows.net/${containerName}?`;
  const base32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getBlockBlobClient = async (blobName) => {
    const sasToken = await getSasToken(containerName);
    const blobUrl = BLOB_SAS_URL + sasToken;
    const blobServiceClient = new BlobServiceClient(blobUrl);
    const containerClient = blobServiceClient.getContainerClient(containerName);
    return containerClient.getBlockBlobClient(blobName);
  };
  const uploadFile = async (file, blobName = file.fileName) => {
    try {
      setUploading(true);
      const blockBlobClient = await getBlockBlobClient(blobName);
      await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: {
          blobContentType: file.type,
        },
      });
      return {
        id: blobName,
        url: blockBlobClient.url,
      };
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setUploading(false);
    }
  };
  const updateTag = async (blobName, tags = {}, metadata = {}) => {
    try {
      await apiService(API_ROUTES.UPDATE_ATTACHMENT_TAGS, "POST", {
        body: { containerName, blobName, tags, metadata },
      });
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  const encodeBase32 = (num: number, len: number) => {
    let str = "";
    for (let i = 0; i < len; i++) {
      str = base32[num % 32] + str;
      num = Math.floor(num / 32);
    }
    return str;
  };

  const uuidToUlid = (uuid: string): string => {
    const hex = uuid.replace(/-/g, "");
    if (hex.length !== 32) throw new Error("Invalid UUID");
    const timestamp = parseInt(hex.slice(0, 12), 16);
    const randomness = hex.slice(12);
    const timePart = encodeBase32(timestamp, 10);
    const randBytes = BufferT.from(randomness.padEnd(20, "0"), "hex");
    let randNum = BigInt("0x" + randBytes.toString("hex"));
    let randPart = "";
    for (let i = 0; i < 16; i++) {
      randPart = base32[Number(randNum % BigInt(32))] + randPart;
      randNum = randNum / BigInt(32);
    }
    randPart = randPart.slice(-16);
    return timePart + randPart;
  };

  const ulidToUuid = (ulidStr: string): string => {
    if (ulidStr.length !== 26) throw new Error("Invalid ULID");
    function decodeBase32(str: string) {
      let num = 0;
      for (let i = 0; i < str.length; i++) {
        num = num * 32 + base32.indexOf(str[i]);
      }
      return num;
    }
    const timestamp = decodeBase32(ulidStr.slice(0, 10));
    const randPart = ulidStr.slice(10);
    let randNum = BigInt(0);
    for (let i = 0; i < randPart.length; i++) {
      randNum = randNum * BigInt(32) + BigInt(base32.indexOf(randPart[i]));
    }
    const tsHex = timestamp.toString(16).padStart(12, "0");
    const randHex = randNum.toString(16).padStart(20, "0").slice(0, 20);
    const hex = tsHex + randHex;
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20, 32),
    ].join("-");
  };

  const SERVICE_CODES = {
    EoR: "EOR",
    Tax: "TAX",
    "Entities +": "ENTITIES_PLUS",
    "Payroll +": "PAYROLL_PLUS",
    Books: "BOOKS",
    Payroll: "PAYROLL",
  };
  const getFileVersion = async (
    tags: CommonMessageAttachmentTags | FileData | CommonOfficerTags,
    containerName = "document-repo",
  ): Promise<number | undefined> => {
    try {
      const data: DocumentVersionResponse = await apiService(
        API_ROUTES.FETCH_DOCUMENT_VERSION,
        "POST",
        {
          body: { containerName, tags },
        },
      );
      return data?.data?.latestVersion;
    } catch (error) {
      console.error(
        "An unexpected error occured while fetching the document version ",
        error,
      );
      setError((error as Error).message);
    }
  };

  const getFormattedProcessName = (processdata) => {
    let date = "",
      sequence = "";
    if (processdata?.start_date) {
      date =
        new Date(processdata?.start_date).getFullYear() +
        new Date(processdata?.start_date).toLocaleDateString("default", {
          month: "2-digit",
        });
    }
    if (processdata?.sequence) {
      sequence = `_${processdata?.sequence}`;
    }
    return `${processdata?.name} ${date}${sequence}`;
  };

  return {
    uploadFile,
    updateTag,
    uploading,
    error,
    ulidToUuid,
    uuidToUlid,
    SERVICE_CODES,
    getFileVersion,
    REQUEST_REASON_CODES,
    getFormattedProcessName,
  };
};

export default useFileUpload;
